import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useKbLastSync } from '@/hooks/useKbLastSync'
import { requestKbRefresh } from '@/lib/api/lupe'
import {
  REFRESH_MAX_WAIT_MS,
  refreshState,
} from '@/lib/compute/solutions-library'

/** "Refresh now" for the kb copy: asks Lupe to sync, then reports progress until a newer sync lands. */
export function useKbRefresh() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [request, setRequest] = useState<{
    at: number
    baseline: string | null
  } | null>(null)
  const [now, setNow] = useState(0)
  const [pending, setPending] = useState(false)
  const outstanding =
    pending || (request !== null && now - request.at <= REFRESH_MAX_WAIT_MS)
  const sync = useKbLastSync({ poll: outstanding })
  const lastSync = sync.data ?? null

  const mutation = useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: async () => requestKbRefresh((await getToken()) ?? ''),
    onMutate: () => setPending(true),
    onSettled: () => setPending(false),
    onSuccess: () => {
      const at = Date.now()
      setNow(at)
      setRequest({ at, baseline: lastSync })
    },
  })

  const derived = refreshState(request, lastSync, now)
  useEffect(() => {
    if (!request) return
    // Wakes the component at the deadline even when polling returns unchanged data.
    const timer = setTimeout(
      () => setNow(request.at + REFRESH_MAX_WAIT_MS + 1),
      REFRESH_MAX_WAIT_MS + 1
    )
    return () => clearTimeout(timer)
  }, [request])
  useEffect(() => {
    if (derived !== 'done') return
    void queryClient.invalidateQueries({ queryKey: ['kb'] })
    setRequest(null)
  }, [derived, queryClient])

  const state = mutation.isPending ? 'starting' : derived
  return {
    refresh: () => mutation.mutate(),
    state,
    error: mutation.error?.message ?? null,
    lastSync,
    syncReady: sync.status === 'success',
  } as const
}
